<script setup lang="ts">
import type { models } from 'cesg-shared';
import { onMounted, type PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ImageViewer from './image-viewer.vue';

const props = defineProps({
  files: {
    type: Array as PropType<models.SupplierInvoiceFile[]>,
    required: true,
  },
  allowMultiple: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const fileUrl = ref<string | null>(null);
const selectedFile = ref<models.SupplierInvoiceFile>(props.files[0]);
const mimeType = ref<string>('application/pdf');
const isErrored = ref(false);
const isFileLoading = ref(true);

const downloadFile = () => {
  try {
    const extension = selectedFile.value.name.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
    };
    mimeType.value = mimeTypes[extension || 'pdf'] || 'application/pdf';

    const base64String = selectedFile.value.blob;
    const binaryString = atob(base64String);
    const byteNumbers = Array.from(binaryString, (char) => char.charCodeAt(0));
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: mimeType.value });
    fileUrl.value = URL.createObjectURL(blob);
    isFileLoading.value = false;
  } catch (error) {
    console.error('Error creating file URL:', error);
    isErrored.value = true;
  }
};

const selectFile = (file: models.SupplierInvoiceFile) => {
  selectedFile.value = file;
  isFileLoading.value = true;
  isErrored.value = false;
  downloadFile();
};

onMounted(downloadFile);
</script>

<template>
  <div class="file-viewer columns">
    <div class="column" :class="files.length > 1 ? 'is-10' : 'is-12'" v-if="fileUrl">
      <embed
        v-if="mimeType === 'application/pdf'"
        :src="fileUrl"
        type="application/pdf"
        width="100%"
        height="800px"
        data-test="pdf-viewer"
      />
      <ImageViewer
        v-else
        :src="fileUrl"
        :alt="selectedFile.name"
        :file-name="selectedFile.name"
        data-test="image-viewer"
      />
    </div>
    <div
      class="file-selector column is-2"
      v-if="files.length > 1"
      data-test="file-selector"
    >
      <h2 class="has-text-weight-bold">{{ t('fileViewer.files') }}:</h2>
      <div class="files">
        <button
          v-for="file in files"
          :key="file.fileId"
          @click="selectFile(file)"
          :class="{ selected: selectedFile.fileId === file.fileId }"
          data-test="file-selector-button"
        >
          {{ file.name }}
        </button>
      </div>
    </div>
    <p v-if="isErrored" data-test="error-text">
      {{ t('fileViewer.errorLoading') }}
    </p>
    <p v-if="isFileLoading" data-test="loading-text">
      {{ t('fileViewer.loading') }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.file-viewer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  height: 100%;

  .file-selector {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;
    width: 400px;

    button {
      background: none;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 4px;
      transition: background 0.2s;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;

      &.selected {
        background: rgba(0, 0, 0, 0.1);
      }

      &:not(:last-child) {
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      }
    }
  }
}
</style>
